<script setup>
import { defineProps } from 'vue';
import { useRouter } from 'vue-router';
import PopperBadge from '@/components/PopperBadge.vue';
import customBadge from '@/components/CustomBadge.vue';
import { useGlobalSearch } from '@/composables/useGlobalSearch';
const router = useRouter();
const {
  getName,
  getPhysicalAddress,
  getIdentifiersVSplit,
  getConnectionStatus,
  getHighwayStatus,
} = useGlobalSearch();

const resolveUrl = (carrier) => {
  const route = router.resolve({
    name: 'Carrier Brokers Show Page',
    params: { id: carrier.id },
  });

  return route.href;
};

const onRowClick = (broker) => {
  console.log('Resolving');
  router.push({
    name: 'Carrier Brokers Show Page',
    params: { id: broker.id },
  });
};

defineProps({
  data: {
    type: Array,
    required: true,
    default() {
      return [];
    },
  },
  loading: Boolean,
});
</script>

<template>
  <main
    v-if="!loading && data?.length == 0"
    class="mt-3 flex h-48 w-full items-center justify-center rounded-md bg-off-white p-4 text-body-color"
  >
    No results found
  </main>
  <main
    v-else
    class="text-left"
  >
    <section
      v-if="!loading"
      class="mt-4 grid grid-cols-6 py-2"
    >
      <div
        class="col-span-2 px-5 text-xs text-dark-gray"
        :class="{ 'animate-pulse': loading }"
      >
        Broker Name
      </div>
      <div
        class="col-span-1 px-5 text-xs text-dark-gray"
        :class="{ 'animate-pulse': loading }"
      >
        HQ
      </div>
      <div
        class="col-span-1 px-5 text-xs text-dark-gray"
        :class="{ 'animate-pulse': loading }"
      >
        Identifiers
      </div>
      <div
        class="col-span-1 px-5 text-xs text-dark-gray"
        :class="{ 'animate-pulse': loading }"
      >
        Authority Age
      </div>
      <div
        class="col-span-1 px-5 text-xs text-dark-gray"
        :class="{ 'animate-pulse': loading }"
      >
        Assessment
      </div>
    </section>
    <section
      v-if="!loading"
      class="rounded-md border border-light-gray"
    >
      <div
        v-for="(broker, index) in data"
        :key="`search-broker-idx-${index}`"
        class="grid cursor-pointer grid-cols-6 items-center hover:bg-lighter-gray"
        :class="{
          'bg-off-white': index % 2 !== 0,
          'rounded-b-md': index % 2 !== 0 && index === data.length - 1,
        }"
        @click="onRowClick(broker)"
      >
        <div class="col-span-2 text-xs text-body-color">
          <div class="flex items-center justify-start">
            <popper-badge
              :highway-status="getHighwayStatus(broker?.broker_company_id)"
              :connection-status="getConnectionStatus(broker)"
            />
            <a
              :href="resolveUrl(broker)"
              @click.stop
            >
              <span class="hover:underline">{{ getName(broker) }}</span>
            </a>
          </div>
        </div>
        <div class="col-span-1 px-5 text-xs text-body-color">
          {{ getPhysicalAddress(broker.physical_address) }}
        </div>
        <div
          class="col-span-1 px-5 text-xs text-body-color"
          v-html="getIdentifiersVSplit(broker.identifiers)"
        />
        <div class="col-span-1 px-5 text-xs text-body-color">
          {{ broker.authority_age }}
        </div>
        <div class="col-span-1 px-5 text-xs text-body-color">
          <custom-badge
            v-if="!loading && broker.overall_broker_assessment == 'fail'"
            bg-color-class="bg-orange-200"
            color-class="text-orange-600"
            size="md"
            class="inline-flex self-start capitalize"
            text="Fail"
          />
          <custom-badge
            v-if="!loading && broker.overall_broker_assessment == 'pass'"
            bg-color-class="bg-green-200"
            color-class="text-green-600"
            size="md"
            class="inline-flex self-start capitalize"
            text="Pass"
          />
          <custom-badge
            v-if="!loading && broker.overall_broker_assessment == 'incomplete'"
            bg-color-class="bg-placeholder-gray"
            color-class="text-dark-gray"
            size="md"
            class="inline-flex self-start capitalize"
            text="Incomplete"
          />
        </div>
      </div>
    </section>
    <section
      v-if="loading"
      class="screenSize mt-4 flex flex-col items-center justify-center gap-3 rounded-md bg-off-white"
    >
      <svg
        class="loading relative h-16 w-16 text-yellow-500"
        viewBox="0 0 32 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M28 16C28 18.3734 27.2962 20.6935 25.9776 22.6668C24.6591 24.6402 22.7849 26.1783 20.5922 27.0866C18.3995 27.9948 15.9867 28.2324 13.6589 27.7694C11.3311 27.3064 9.19295 26.1635 7.51472 24.4853C5.83649 22.8071 4.6936 20.6689 4.23058 18.3411C3.76755 16.0133 4.00519 13.6005 4.91345 11.4078C5.8217 9.21509 7.35977 7.34094 9.33316 6.02236C11.3065 4.70379 13.6266 4 16 4"
          stroke="currentColor"
          stroke-width="3"
        />
        <path
          d="M16 1V7L20 4L16 1Z"
          fill="currentColor"
        />
        <path
          d="M22.4895 7.161C24.4444 8.50678 25.9564 10.402 26.8342 12.6071"
          stroke="currentColor"
          stroke-width="3"
        />
      </svg>
      <h3 class="text-center text-xl text-dark-gray">
        Loading broker search results
      </h3>
    </section>
  </main>
</template>
<style scoped>
.screenSize {
  height: 50vh;
}
</style>
